import { useEffect, useRef, useState } from "react";
import { Icon, type IconName } from "./Icon";
import { Brand } from "./ui";
import { LanguagePicker } from "./LanguagePicker";
import { PATHWAY_IDS } from "./EnrolmentChoice";
import { useI18n } from "../lib/i18n";
import { PATHWAY_BLOCKS } from "../lib/pathways";
import { initialsFor, today } from "../lib/data";
import {
  AGE_CHOICES,
  CHILD_COLOURS,
  childProblem,
  draftAge,
  learnerInputFor,
  levelForAge,
  newChild,
  type ChildDraft,
} from "../lib/onboarding";
import type { Level } from "../lib/curriculum";
import type { CurriculumPathway, LearnerProfileInput } from "../types";

/**
 * The first five minutes, as two questions.
 *
 * ── What it replaces ────────────────────────────────────────────────────────
 * A single modal that asked for everything at once: name, level, who teaches,
 * a pathway, a block editor, delivery, a birthday, a colour, and "more
 * detail". Every one of those has a reason to exist; together, at the moment
 * somebody has just made an account, they were a wall.
 *
 * Now: which curriculum, then who the children are — name and age, a colour
 * because children care about that, and nothing else. Their class is filled in
 * from their age, visibly, so a parent can correct it rather than work it out.
 * Everything the old form asked beyond that is still on the family page, for
 * when somebody wants to give it.
 *
 * ── Decisions that are deliberately not asked here ──────────────────────────
 * Who teaches. Every family starts parent-led, which is what signing up here
 * is; an Al Noor teacher is requested per subject from the curriculum page, at
 * the point a family actually knows which subject they need help with.
 *
 * ── Several children ────────────────────────────────────────────────────────
 * Brothers and sisters are added in the same step rather than one modal at a
 * time afterwards. They share the curriculum chosen in step one; each child's
 * can still be changed on its own later. They are saved one at a time and a
 * saved child is never saved twice, so a dropped connection halfway through
 * costs a retry, not a duplicate.
 */

const PATHWAY_ICONS: Record<CurriculumPathway, IconName> = {
  balanced: "leaf",
  pakistan: "book",
  cambridge: "target",
  faith: "heart",
  future_skills: "brain",
};

const MAX_CHILDREN = 6;

type Step = "curriculum" | "children" | "building" | "ready";

export function Onboarding({
  guardianName,
  levels,
  onCreateChild,
  onFinished,
  onSignOut,
}: {
  guardianName: string;
  /** Playgroup through Class 5, in order. */
  levels: Level[];
  /** Saves one child and resolves with the new learner's id. */
  onCreateChild: (input: LearnerProfileInput) => Promise<string>;
  /** Everything is saved: open the app on this child. */
  onFinished: (firstLearnerId: string) => void;
  onSignOut: () => void;
}) {
  const { t, locale } = useI18n();
  const [step, setStep] = useState<Step>("curriculum");
  const [pathway, setPathway] = useState<CurriculumPathway | null>(null);
  const [children, setChildren] = useState<ChildDraft[]>(() => [newChild(0)]);
  const [showProblems, setShowProblems] = useState(false);
  const [problem, setProblem] = useState("");
  /** draft key -> saved learner id. A ref, so a retry reads what the last attempt really saved. */
  const saved = useRef(new Map<string, string>());
  /** The same keys as state, because the screen ticks each child off as it lands. */
  const [savedKeys, setSavedKeys] = useState<string[]>([]);
  const heading = useRef<HTMLHeadingElement>(null);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  // Move focus to the new question, so a screen reader announces it and a
  // keyboard user is not left on a button that no longer exists. Not on the
  // children step: there the first name field takes it, ready for typing.
  useEffect(() => {
    if (step !== "children") heading.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [step]);

  const firstName = guardianName.trim().split(/\s+/)[0] || "";
  const subjectLabel = (subject: string) => {
    if (locale !== "ur") return subject;
    const translated = t(`subject.${subject}`);
    return translated === `subject.${subject}` ? subject : translated;
  };
  const levelTitle = (level: Level) => (locale === "ur" ? t(`level.${level.slug}.title`) : level.title);
  const levelAges = (level: Level) => (locale === "ur" ? t(`level.${level.slug}.ages`) : level.subtitle ?? "");

  function updateChild(key: string, patch: Partial<ChildDraft>) {
    setChildren((current) => current.map((child) => (child.key === key ? { ...child, ...patch } : child)));
  }

  /** An age, or a birthday, places the child — the class follows it, visibly. */
  function placeBy(child: ChildDraft, patch: Partial<ChildDraft>) {
    const next = { ...child, ...patch };
    const age = draftAge(next);
    const level = age === null ? null : levelForAge(age, levels);
    updateChild(child.key, { ...patch, levelSlug: level?.slug ?? next.levelSlug });
  }

  function addChild() {
    setChildren((current) => (current.length >= MAX_CHILDREN ? current : [...current, newChild(current.length)]));
  }

  function removeChild(key: string) {
    setChildren((current) => (current.length > 1 ? current.filter((child) => child.key !== key) : current));
  }

  async function createAll() {
    if (!pathway) {
      setStep("curriculum");
      return;
    }
    if (children.some((child) => childProblem(child, levels))) {
      setShowProblems(true);
      return;
    }
    setProblem("");
    setStep("building");
    try {
      for (const child of children) {
        if (saved.current.has(child.key)) continue;
        const id = await onCreateChild(learnerInputFor(child, pathway, levels));
        saved.current.set(child.key, id);
        if (live.current) setSavedKeys((current) => [...current, child.key]);
      }
      if (!live.current) return;
      setStep("ready");
      const first = saved.current.get(children[0].key);
      // Long enough to read "All set", short enough not to be a wait.
      window.setTimeout(() => {
        if (first && live.current) onFinished(first);
      }, 1100);
    } catch (cause) {
      if (!live.current) return;
      setProblem(cause instanceof Error && cause.message ? cause.message : t("error.generic"));
      setStep("children");
    }
  }

  const names = children.map((child) => child.name.trim()).filter(Boolean);
  const stepIndex = step === "curriculum" ? 0 : 1;

  return (
    <main className="onboard">
      <div className="onboard-glow" aria-hidden="true" />
      <header className="onboard-top">
        <Brand />
        {(step === "curriculum" || step === "children") && (
          <ol className="onboard-progress" aria-label={t("onboard.progress")}>
            {["curriculum", "children"].map((id, index) => (
              <li
                key={id}
                className={index === stepIndex ? "current" : index < stepIndex ? "done" : ""}
                aria-current={index === stepIndex ? "step" : undefined}
              >
                <i aria-hidden="true">{index < stepIndex ? <Icon name="check" size={13} /> : index + 1}</i>
                <span>{t(`onboard.progress.${id}`)}</span>
              </li>
            ))}
          </ol>
        )}
        <div className="onboard-top-actions">
          <LanguagePicker />
          <button type="button" className="link-button" onClick={onSignOut}>
            {t("side.signOut")}
          </button>
        </div>
      </header>

      {step === "curriculum" && (
        <section className="onboard-panel" key="curriculum" aria-labelledby="onboard-curriculum-title">
          <p className="eyebrow">{firstName ? t("onboard.hello", { name: firstName }) : t("onboard.helloPlain")}</p>
          <h1 id="onboard-curriculum-title" ref={heading} tabIndex={-1}>
            {t("onboard.curriculum.title")}
          </h1>
          <p className="onboard-lede">{t("onboard.curriculum.lede")}</p>

          <div className="onboard-pathways" role="group" aria-label={t("onboard.curriculum.title")}>
            {PATHWAY_IDS.map((id) => (
              <button
                type="button"
                key={id}
                aria-pressed={pathway === id}
                className={pathway === id ? "onboard-pathway selected" : "onboard-pathway"}
                onClick={() => setPathway(id)}
                // A second tap on the card already chosen means "yes, this one".
                onDoubleClick={() => {
                  setPathway(id);
                  setStep("children");
                }}
              >
                <span className="onboard-pathway-top">
                  <span className="onboard-pathway-icon">
                    <Icon name={PATHWAY_ICONS[id]} size={20} />
                  </span>
                  {id === "balanced" && <span className="onboard-badge">{t("onboard.recommended")}</span>}
                  <span className="onboard-check" aria-hidden="true">
                    <Icon name="check" size={14} />
                  </span>
                </span>
                <strong>{t(`enrol.pathway.${id}.title`)}</strong>
                <span className="onboard-pathway-summary">{t(`enrol.pathway.${id}.summary`)}</span>
                <span className="onboard-subjects">
                  {PATHWAY_BLOCKS[id].map((line) => (
                    <i key={line.subject}>{subjectLabel(line.subject)}</i>
                  ))}
                </span>
              </button>
            ))}
          </div>

          <p className="onboard-footnote">
            <Icon name="shield" size={15} /> {t("onboard.curriculum.note")}
          </p>

          <div className="onboard-actions">
            <span className="onboard-actions-hint">
              {pathway ? t(`enrol.pathway.${pathway}.title`) : t("onboard.curriculum.pick")}
            </span>
            <button type="button" className="primary" disabled={!pathway} onClick={() => setStep("children")}>
              {t("onboard.continue")} <Icon name="arrow" />
            </button>
          </div>
        </section>
      )}

      {step === "children" && (
        <form
          className="onboard-panel"
          key="children"
          aria-labelledby="onboard-children-title"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void createAll();
          }}
        >
          <p className="eyebrow">{pathway ? t(`enrol.pathway.${pathway}.title`) : ""}</p>
          <h1 id="onboard-children-title" ref={heading} tabIndex={-1}>
            {children.length > 1 ? t("onboard.children.titleMany") : t("onboard.children.title")}
          </h1>
          <p className="onboard-lede">{t("onboard.children.lede")}</p>

          <div className="onboard-children">
            {children.map((child, index) => {
              // Both at once: fixing the name only to be told about the age
              // next is two round trips for one form.
              const missingName = showProblems && !child.name.trim();
              const missingAge = showProblems && levels.length > 0 && !child.levelSlug;
              const level = levels.find((candidate) => candidate.slug === child.levelSlug) ?? null;
              const nameId = `${child.key}-name`;
              return (
                <article key={child.key} className={`onboard-child${savedKeys.includes(child.key) ? " saved" : ""}`}>
                  <div className="onboard-child-head">
                    <span className={`onboard-avatar ${child.colour}`} aria-hidden="true">
                      {child.name.trim() ? initialsFor(child.name) : <Icon name="users" size={18} />}
                    </span>
                    <div className="onboard-name">
                      <label htmlFor={nameId}>
                        {children.length > 1 ? t("onboard.childN", { n: index + 1 }) : t("onboard.childName")}
                      </label>
                      <input
                        id={nameId}
                        value={child.name}
                        onChange={(event) => updateChild(child.key, { name: event.target.value })}
                        placeholder={t("onboard.childNamePlaceholder")}
                        autoComplete="off"
                        autoFocus={index === children.length - 1}
                        aria-invalid={missingName}
                        disabled={savedKeys.includes(child.key)}
                      />
                      {missingName && <small className="onboard-field-problem">{t("learner.needName")}</small>}
                    </div>
                    {children.length > 1 && !savedKeys.includes(child.key) && (
                      <button
                        type="button"
                        className="onboard-remove"
                        onClick={() => removeChild(child.key)}
                        aria-label={t("onboard.removeChild", { name: child.name.trim() || t("onboard.childN", { n: index + 1 }) })}
                      >
                        <Icon name="close" size={16} />
                      </button>
                    )}
                  </div>

                  <div className="onboard-field">
                    <span className="onboard-label">
                      {child.birthday !== null ? t("onboard.birthday") : t("onboard.age")}
                    </span>
                    {child.birthday === null ? (
                      <div className="onboard-chips ages" role="group" aria-label={t("onboard.age")}>
                        {AGE_CHOICES.map((age) => (
                          <button
                            type="button"
                            key={age}
                            aria-pressed={child.age === age}
                            className={child.age === age ? "selected" : ""}
                            onClick={() => placeBy(child, { age })}
                          >
                            {age === AGE_CHOICES[AGE_CHOICES.length - 1] ? `${age}+` : age}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <input
                        type="date"
                        className="onboard-date"
                        value={child.birthday}
                        max={today()}
                        onChange={(event) => placeBy(child, { birthday: event.target.value })}
                        aria-label={t("onboard.birthday")}
                      />
                    )}
                    <button
                      type="button"
                      className="link-button onboard-switch"
                      onClick={() =>
                        child.birthday === null
                          ? updateChild(child.key, { birthday: "" })
                          : placeBy(child, { birthday: null })
                      }
                    >
                      {child.birthday === null ? t("onboard.useBirthday") : t("onboard.useAge")}
                    </button>
                    {missingAge && <small className="onboard-field-problem">{t("onboard.needAge")}</small>}
                  </div>

                  {levels.length > 0 && (level || draftAge(child) !== null) && (
                    <div className="onboard-field onboard-class">
                      <label className="onboard-label" htmlFor={`${child.key}-level`}>
                        {t("onboard.class")}
                      </label>
                      <div className="onboard-class-row">
                        <select
                          id={`${child.key}-level`}
                          value={child.levelSlug ?? ""}
                          onChange={(event) => updateChild(child.key, { levelSlug: event.target.value || null })}
                        >
                          {!child.levelSlug && <option value="">{t("onboard.classPick")}</option>}
                          {levels.map((option) => (
                            <option key={option.slug} value={option.slug}>
                              {levelTitle(option)}
                              {levelAges(option) ? ` · ${levelAges(option)}` : ""}
                            </option>
                          ))}
                        </select>
                        <small>{t("onboard.classHint")}</small>
                      </div>
                    </div>
                  )}

                  <div className="onboard-field-row">
                    <div className="onboard-field">
                      <span className="onboard-label">
                        {t("onboard.gender")} <em>{t("onboard.optional")}</em>
                      </span>
                      <div className="onboard-chips" role="group" aria-label={t("onboard.gender")}>
                        {(["female", "male"] as const).map((gender) => (
                          <button
                            type="button"
                            key={gender}
                            aria-pressed={child.gender === gender}
                            className={child.gender === gender ? "selected" : ""}
                            onClick={() => updateChild(child.key, { gender: child.gender === gender ? null : gender })}
                          >
                            {t(`learner.gender.${gender}`)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="onboard-field">
                      <span className="onboard-label">{t("learner.colour")}</span>
                      <div className="onboard-swatches" role="group" aria-label={t("learner.colour")}>
                        {CHILD_COLOURS.map((colour) => (
                          <button
                            type="button"
                            key={colour}
                            className={`onboard-swatch ${colour}`}
                            aria-pressed={child.colour === colour}
                            aria-label={t(`onboard.colour.${colour}`)}
                            onClick={() => updateChild(child.key, { colour })}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {children.length < MAX_CHILDREN && (
            <button type="button" className="onboard-add" onClick={addChild}>
              <Icon name="plus" size={16} /> {t("onboard.children.add")}
            </button>
          )}

          {problem && (
            <p className="form-problem" role="alert">
              {t("onboard.saveProblem")} {problem}
            </p>
          )}

          <div className="onboard-actions">
            <button type="button" className="soft-button" onClick={() => setStep("curriculum")}>
              <span className="onboard-back" aria-hidden="true">
                <Icon name="arrow" size={16} />
              </span>
              {t("onboard.back")}
            </button>
            <button type="submit" className="primary">
              {names.length === 1
                ? t("onboard.createOne", { name: names[0] })
                : names.length > 1
                  ? t("onboard.createMany", { count: names.length })
                  : t("learner.start")}{" "}
              <Icon name="arrow" />
            </button>
          </div>
        </form>
      )}

      {(step === "building" || step === "ready") && (
        <section className="onboard-panel onboard-finish" role="status" aria-live="polite" key="finish">
          <div className={step === "ready" ? "onboard-orbit done" : "onboard-orbit"} aria-hidden="true">
            <span />
            <Icon name={step === "ready" ? "check" : "spark"} size={30} />
          </div>
          <h1 ref={heading} tabIndex={-1}>
            {step === "ready"
              ? t("onboard.ready.title")
              : names.length > 1
                ? t("onboard.building.many")
                : t("onboard.building.one", { name: names[0] ?? "" })}
          </h1>
          <p className="onboard-lede">{step === "ready" ? t("onboard.ready.copy") : t("onboard.building.copy")}</p>
          <ul className="onboard-roll">
            {children.map((child) => {
              const level = levels.find((candidate) => candidate.slug === child.levelSlug);
              return (
                <li key={child.key} className={savedKeys.includes(child.key) ? "done" : ""}>
                  <span className={`onboard-avatar small ${child.colour}`} aria-hidden="true">
                    {initialsFor(child.name)}
                  </span>
                  <strong>{child.name.trim()}</strong>
                  {level && <small>{levelTitle(level)}</small>}
                  <i aria-hidden="true">{savedKeys.includes(child.key) ? <Icon name="check" size={14} /> : null}</i>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </main>
  );
}
